import React, { useEffect, useRef, useCallback } from 'react';
import { noop } from 'lodash';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import filterChildren from '../../utils/Children/filterChildren';
import Icon from '../Icon/Icon';

function getHeight(topOffset) {
  return {
    height: `calc(100% - ${topOffset})`,
  };
}

const Drawer = ({
  id,
  className,
  children,
  open,
  secondary,
  hideCloseButton,
  style,
  title,
  topOffset,
  width,
  theme,
  onClose,
  onEscClick,
}) => {
  const drawerRef = useRef(null);

  const handleEscClick = useCallback((event) => {
    if (event.keyCode === 27 && open) {
      const closeFn = onEscClick || onClose;
      closeFn();
    }
  }, [open, onEscClick, onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleEscClick, false);
    if (open && drawerRef.current) {
      drawerRef.current.focus();
    }

    return () => {
      document.removeEventListener('keydown', handleEscClick);
    };
  }, [open, handleEscClick]);

  useEffect(() => {
    if (open && drawerRef.current) {
      drawerRef.current.focus();
    }
  }, [open]);

  const handleClose = useCallback(() => {
    if (open) {
      onClose();
    }
  }, [open, onClose]);

  const drawerClass = classNames(
    'fixed top-0 h-full transition-transform duration-500 z-drawer',
    {
      'left-0 transform': !secondary,
      '-translate-x-full': !open && !secondary,
      'right-0 transform': secondary,
      'translate-x-full': !open && secondary,
      'translate-x-0': open,
      'bg-white': !secondary,
    },
    className,
  );

  const buttonClass = classNames(
    'absolute top-2 transform h-8 w-8 rounded-full border border-gray-300 bg-white transition-transform duration-750 z-modal flex items-center justify-center mt-2 cursor-pointer',
    {
      'left-full ml-2 -translate-x-0': !secondary && open,
      'right-full mr-2 translate-x-0': secondary && open,
      '-translate-x-full': !open && !secondary,
      'translate-x-full': !open && secondary,
      'bg-gray-800': theme === 'dark',
    },
  );

  const newChildren = filterChildren(children);

  return (
    <div
      ref={drawerRef}
      id={id}
      className={drawerClass}
      style={{ ...style, top: topOffset, width, ...getHeight(topOffset) }}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? `${id}-title` : undefined}
    >
      <div className="relative h-full">
        <div className="absolute z-drawer h-full w-full bg-white shadow-xl overflow-y-auto overflow-x-hidden">
          <div className="flex flex-row items-start">
            {title && (
              <div className="flex-grow p-2">
                {title}
              </div>
            )}
          </div>
          {newChildren}
        </div>
        {!hideCloseButton && (
          <div className={buttonClass} onClick={handleClose}>
            <Icon
              ariaLabel="Close"
              icon="Cross"
              size={14}
              color={theme === 'dark' ? 'white' : 'medium-border-blue'}
            />
          </div>
        )}
      </div>
    </div>
  );
};

Drawer.propTypes = {
  /** id for reference */
  id: PropTypes.string,
  /** className for reference */
  className: PropTypes.string,
  /** If true, drawer hides close button.  Defaults to false. */
  hideCloseButton: PropTypes.bool,
  /** When close button is clicked or Esc key pressed, run this function */
  onClose: PropTypes.func,
  /** When Esc key is pressed, run this function. Takes precedence over onClose function. */
  onEscClick: PropTypes.func,
  /** If true, the Drawer is open. */
  open: PropTypes.bool.isRequired,
  /** If true, the Drawer is positioned to open from the opposite side. */
  secondary: PropTypes.bool,
  /** Override the inline-styles of the root element || Not Recommended */
  style: PropTypes.object,
  /** The drawer title string or node */
  title: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
  ]),
  /** How much to offset drawer from top of window */
  topOffset: PropTypes.string,
  /** The width of the Drawer in pixels or percentage in string format ex. 50% to fill half of the window or 100% and so on. */
  width: PropTypes.string,
  /**
   * children props for content inside
   * @ignore
   */
  children: PropTypes.node,
  /** theme to determine light or dark mode */
  theme: PropTypes.oneOf(['light', 'dark']),
};

Drawer.defaultProps = {
  children: [],
  className: '',
  id: '',
  onClose: noop,
  onEscClick: undefined,
  secondary: false,
  style: {},
  hideCloseButton: false,
  title: null,
  topOffset: '0',
  width: '20%',
  theme: 'light',
};

export default Drawer;
